/**
 * Direct FlatBuffer fetch+decode for a single adapter, bypassing RTK Query.
 *
 * RTK Query's lazy query trigger replaces its subscription on each call,
 * so concurrent `trigger()` calls in `Promise.all` silently drop all but
 * the last adapter's result.  Even `dispatch(initiate)` resolves with
 * undefined for certain adapters.  This direct fetch is the reliable path
 * for concurrent adapter fetches in the setup wizard.
 *
 * Because it bypasses RTK Query it also bypasses RTK Query's `retry`
 * middleware. The setup wizard fires a `Promise.all` of these (one per
 * bank-referenced adapter) during its initial load; under that burst an
 * individual request can transiently reject with `TypeError: Failed to fetch`
 * (connection dropped/refused under load) — observed deterministically on one
 * adapter of a concurrent batch while an immediate re-request succeeds. A
 * transient blip must NOT degrade the Added-Adapters row to a blank/Pending
 * fallback, so the transport (fetch + body read) is retried a few times with a
 * short backoff. HTTP error statuses and decode failures are NOT retried —
 * neither is transient.
 */
import { extractEnvelopeWithHash } from "../envelope";
import { responseSchemaRefusal } from "./apiVerdicts";
import { getSchemaHandler } from "./schemaRegistry";
import { noteServerSchema, responseSchema, schemaRequestHeaders } from "./serverSchemas";

const MAX_TRANSPORT_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 150;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** A successful response's bytes, and the API schema it names, if any. */
interface AdapterBody {
  bytes: Uint8Array;
  schema: string | null;
}

/**
 * Fetch the raw adapter bytes with transient-failure retry. Returns null for a
 * definitive HTTP error (non-2xx), the response bytes on success, or throws the
 * last network error once the retries are exhausted. Written recursively rather
 * than as an await-in-loop: retries are inherently sequential (each must wait
 * for the prior), so there is nothing to parallelize.
 */
async function fetchAdapterBytes(
  url: string,
  headers: Record<string, string>,
  attempt = 0,
): Promise<AdapterBody | null> {
  try {
    const resp = await fetch(url, { headers });
    if (!resp.ok) return null;
    return { bytes: new Uint8Array(await resp.arrayBuffer()), schema: responseSchema(resp) };
  } catch (err) {
    if (attempt >= MAX_TRANSPORT_RETRIES) throw err;
    await delay(RETRY_BASE_DELAY_MS * (attempt + 1));
    return fetchAdapterBytes(url, headers, attempt + 1);
  }
}

/**
 * Throws when the server wrote the adapter with an API schema whose Adapter
 * this bundle cannot read unchanged (see `apiVerdicts.ts`).
 */
export default async function fetchAdapterDirect(
  id: string,
  token: string,
): Promise<Record<string, unknown> | null> {
  const baseUrl = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "";
  const body = await fetchAdapterBytes(`${baseUrl}/adapters/${id}`, {
    authorization: token,
    Accept: "application/flatbuffers",
    ...schemaRequestHeaders(baseUrl),
  });
  if (!body) return null;
  noteServerSchema(baseUrl, body.schema);
  const envelope = extractEnvelopeWithHash(body.bytes);
  if (!envelope) return null;
  const handler = getSchemaHandler(envelope.schemaHash);
  if (!handler) return null;
  const refusal = await responseSchemaRefusal(baseUrl, body.schema, handler.typeName, token);
  if (refusal !== null) {
    throw new Error(refusal);
  }
  handler.verify(envelope.payload);
  const unpacked = handler.unpack(envelope.payload) as Record<string, unknown> | null;
  return (unpacked?.data ?? unpacked) as Record<string, unknown> | null;
}
